import React, { useState, useEffect, useMemo } from "react";
import { useParams } from "react-router-dom";
import styles from './StorageBox.module.css';
import { saveAs } from 'file-saver';
import { utils, write } from 'xlsx';
import { useTranslation } from 'react-i18next';
import EditBoxMemo from "../../components/modal/EditBoxMemo";
import BankingInfoTab from "./BankingInfoTab";

// image
import editPen from '../../img/common/orangePen.png';
import downloadIcon from '../../img/common/downloadIcon.png';
import checkImage from "../../img/common/black-checkmark.png";

function StorageBox() {
    // ---------- 상태 ----------
    const [checkMode, setCheckMode] = useState(false);
    const [selectedPositions, setSelectedPositions] = useState([]);
    const { t } = useTranslation();
    const [authLevel, setAuthLevel] = useState("");
    const [refresh, setRefresh] = useState(0);
    const { vessel, line, box } = useParams();
    const [boxData, setBoxData] = useState([]);
    const [selectedData, setSelectedData] = useState(null);
    const [boxMemo, setBoxMemo] = useState('');
    const [editMemo, setEditMemo] = useState(false);
    const [selectedOrganoidKey, setSelectedOrganoidKey] = useState(null);

    // ---------- 유틸 ----------
    const formatDate = (dateString) => {
        const d = new Date(dateString);
        const z = (n, w = 2) => String(n).padStart(w, '0');
        return `${d.getFullYear()}-${z(d.getMonth() + 1)}-${z(d.getDate())} ${z(d.getHours())}:${z(d.getMinutes())}:${z(d.getSeconds())}.${String(d.getMilliseconds()).padStart(3, '0')}`;
    };

    const togglePosition = (pos) => {
        setSelectedPositions(prev => prev.includes(pos) ? prev.filter(p => p !== pos) : [...prev, pos]);
    };

    const saveMemo = (text) => {
        setBoxMemo(text);
        fetch("/api/setBoxMemo", {
            method: "post",
            headers: { "content-type": "application/json" },
            body: JSON.stringify({ vessel, line, box, memo: text })
        }).then(r => r.json()).catch(err => console.error("...", err));
    };

    // ---------- 색상 유틸 (sRGB <-> Lab, ΔE CIE76) ----------
    const hexToRgb = (hex) => {
        let m = hex.replace('#', '');
        if (m.length === 3) m = m.split('').map(c => c + c).join('');
        const n = parseInt(m, 16);
        return { r: (n >> 16) & 255, g: (n >> 8) & 255, b: n & 255 };
    };
    const rgbToHex = ({ r, g, b }) => '#' + [r, g, b].map(v => Math.round(v).toString(16).padStart(2, '0')).join('');

    const srgbToLinear = c => {
        c /= 255;
        return c <= 0.04045 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
    };
    const rgbToLab = (r, g, b) => {
        const R = srgbToLinear(r), G = srgbToLinear(g), B = srgbToLinear(b);
        // D65
        let x = R * 0.4124 + G * 0.3576 + B * 0.1805;
        let y = R * 0.2126 + G * 0.7152 + B * 0.0722;
        let z = R * 0.0193 + G * 0.1192 + B * 0.9505;
        x /= 0.95047; y /= 1.00000; z /= 1.08883;
        const f = t => t > 0.008856 ? Math.cbrt(t) : (7.787 * t) + 16 / 116;
        const fx = f(x), fy = f(y), fz = f(z);
        return { L: 116 * fy - 16, a: 500 * (fx - fy), b: 200 * (fy - fz) };
    };
    const deltaE = (lab1, lab2) => {
        const dL = lab1.L - lab2.L, da = lab1.a - lab2.a, db = lab1.b - lab2.b;
        return Math.sqrt(dL * dL + da * da + db * db);
    };

    // HSL(0~360,0~1,0~1) -> RGB(0~255)
    const hslToRgb = (h, s, l) => {
        const c = (1 - Math.abs(2 * l - 1)) * s;
        const hp = (h % 360) / 60;
        const x = c * (1 - Math.abs(hp % 2 - 1));
        let r = 0, g = 0, b = 0;
        if (0 <= hp && hp < 1) [r, g, b] = [c, x, 0];
        else if (1 <= hp && hp < 2) [r, g, b] = [x, c, 0];
        else if (2 <= hp && hp < 3) [r, g, b] = [0, c, x];
        else if (3 <= hp && hp < 4) [r, g, b] = [0, x, c];
        else if (4 <= hp && hp < 5) [r, g, b] = [x, 0, c];
        else[r, g, b] = [c, 0, x];
        const m = l - c / 2;
        return { r: (r + m) * 255, g: (g + m) * 255, b: (b + m) * 255 };
    };

    // ---------- 기본 팔레트 (시드 + 골든앵글 보충) ----------
    const SEEDS = [
        // Okabe–Ito (black 제외) + Tableau10 일부
        '#E69F00', '#56B4E9', '#009E73', '#F0E442', '#0072B2', '#D55E00', '#CC79A7',
        '#4E79A7', '#F28E2B', '#E15759', '#76B7B2', '#59A14F', '#EDC948', '#B07AA1', '#FF9DA7', '#9C755F', '#BAB0AC'
    ];
    const GOLDEN_ANGLE = 137.508;
    const MIN_DE_GLOBAL = 25; // 기본 전역 분산 보장

    const minDeltaToPalette = (lab, labs) =>
        labs.length ? Math.min(...labs.map(L => deltaE(lab, L))) : Infinity;

    const buildDistinctPalette = (n) => {
        const out = [];
        const labs = [];
        for (let i = 0; i < SEEDS.length && out.length < n; i++) {
            const rgb = hexToRgb(SEEDS[i]);
            const lab = rgbToLab(rgb.r, rgb.g, rgb.b);
            if (minDeltaToPalette(lab, labs) >= MIN_DE_GLOBAL) {
                out.push(SEEDS[i]);
                labs.push(lab);
            }
        }
        let i = 0;
        while (out.length < n && i < 2000) {
            const hue = (i * GOLDEN_ANGLE) % 360;
            const sat = 0.74;                     // 채도 고정 (밝기 변형 최소화)
            const light = (i % 2 ? 0.56 : 0.50);  // 약간의 명도 교차
            const rgb = hslToRgb(hue, sat, light);
            const lab = rgbToLab(rgb.r, rgb.g, rgb.b);
            if (minDeltaToPalette(lab, labs) >= MIN_DE_GLOBAL) {
                out.push(rgbToHex(rgb));
                labs.push(lab);
            }
            i++;
        }
        return out;
    };

    // ---------- 인접 그래프 만들기 (상하좌우) ----------
    function buildAdjacency(boxData) {
        // pos -> key
        const posKey = new Map();
        boxData.forEach(d => {
            if (d?.Position && d?.Organoid_Info_Key) posKey.set(Number(d.Position), d.Organoid_Info_Key);
        });

        const adj = new Map(); // keyA -> Map(keyB -> weight)
        const addEdge = (a, b) => {
            if (!a || !b || a === b) return;
            if (!adj.has(a)) adj.set(a, new Map());
            if (!adj.has(b)) adj.set(b, new Map());
            adj.get(a).set(b, (adj.get(a).get(b) || 0) + 1);
            adj.get(b).set(a, (adj.get(b).get(a) || 0) + 1);
        };

        for (let p = 1; p <= 100; p++) {
            const a = posKey.get(p);
            if (!a) continue;
            const row = Math.floor((p - 1) / 10), col = (p - 1) % 10;

            const neighbors = [];
            if (col > 0) neighbors.push(p - 1);
            if (col < 9) neighbors.push(p + 1);
            if (row > 0) neighbors.push(p - 10);
            if (row < 9) neighbors.push(p + 10);

            neighbors.forEach(q => {
                const b = posKey.get(q);
                if (b) addEdge(a, b);
            });
        }
        return adj;
    }

    // ---------- 인접 대비 최적화로 키 → 색상 배정 ----------
    function assignColorsWithAdjacency(keys, palette, adj) {
        // 준비: 색상 후보(hex, lab)
        const candidates = palette.map(hex => ({ hex, lab: rgbToLab(...Object.values(hexToRgb(hex))) }));

        // 키의 연결 정도(가중치 합)로 정렬 (연결 많은 키 먼저)
        const degree = new Map(keys.map(k => [k, 0]));
        keys.forEach(k => {
            const m = adj.get(k);
            if (m) degree.set(k, Array.from(m.values()).reduce((a, b) => a + b, 0));
        });
        const order = [...keys].sort((a, b) => {
            const da = degree.get(a) || 0, db = degree.get(b) || 0;
            return db - da || a.localeCompare(b);
        });

        const assigned = new Map();      // key -> {hex, lab}
        const usedIdx = new Set();

        // 평가 함수: 이웃들과의 최소 ΔE(가중) 최대화
        const scoreCandidate = (key, cand) => {
            const neigh = adj.get(key);
            // 이웃이 이미 배정된 경우: 가중 최소거리 극대화
            if (neigh && Array.from(neigh.keys()).some(nk => assigned.has(nk))) {
                let minWeighted = Infinity;
                for (const [nk, w] of neigh) {
                    if (!assigned.has(nk)) continue;
                    const d = deltaE(cand.lab, assigned.get(nk).lab);
                    // 자주 맞닿을수록 더 멀게: 가중치^0.5 정도로 늘려 반영
                    const wd = d / Math.sqrt(Math.max(1, w));
                    if (wd < minWeighted) minWeighted = wd;
                }
                // 이웃이 하나도 배정 안됐으면 below 로 처리
                if (minWeighted < Infinity) return minWeighted;
            }
            // 전역 분산: 기존 배정들과 최소거리
            if (assigned.size === 0) return 9999;
            let minD = Infinity;
            for (const { lab } of assigned.values()) {
                const d = deltaE(cand.lab, lab);
                if (d < minD) minD = d;
            }
            return minD;
        };

        for (const k of order) {
            // 아직 배정 안된 색 후보 중에서 최고 점수 선택
            let bestIdx = -1, bestScore = -1;
            for (let i = 0; i < candidates.length; i++) {
                if (usedIdx.has(i)) continue;
                const sc = scoreCandidate(k, candidates[i]);
                if (sc > bestScore) { bestScore = sc; bestIdx = i; }
            }
            if (bestIdx === -1) bestIdx = [...Array(candidates.length).keys()].find(i => !usedIdx.has(i)) ?? 0;
            assigned.set(k, candidates[bestIdx]);
            usedIdx.add(bestIdx);
        }

        const result = {};
        keys.forEach(k => { result[k] = assigned.get(k).hex; });
        return result;
    }

    // ---------- 키 수에 맞춰 팔레트 만들고, 인접 최적화로 색 배정 ----------
    const colorMap = useMemo(() => {
        const keys = Array.from(
            new Set(boxData.filter(d => d?.Organoid_Info_Key).map(d => d.Organoid_Info_Key))
        ).sort();
        if (keys.length === 0) return {};

        const adj = buildAdjacency(boxData);
        const palette = buildDistinctPalette(keys.length + 4); // 여유 4개
        return assignColorsWithAdjacency(keys, palette, adj);
    }, [boxData]);

    const getColorForOrganoid = (organoidKey) => colorMap[organoidKey] || '#cccccc';

    // ---------- 다운로드 ----------
    const handleSubmit = () => {
        fetch("/api/getBankingListByStorage", {
            method: "post",
            headers: { "content-type": "application/json" },
            body: JSON.stringify({ vessel, line, box })
        })
            .then(r => r.json())
            .then(data => {
                const exportData = checkMode
                    ? data.filter(el => selectedPositions.includes(el.Position))
                    : data;

                exportData.forEach(e => {
                    e.time = formatDate(e.time);
                    e.url = ("https://banking.gradiantbio.com/organoid/" + e.Organoid_Info_Key + "/bank/" + e.time).replace(' ', '%20');
                    e.Organoid_Info_Key = e.Organoid_Info_Key.replace(/hashmark/g, '#');
                });

                const ws = utils.json_to_sheet(exportData);
                const wb = utils.book_new();
                utils.book_append_sheet(wb, ws, "Sheet1");
                const wbout = write(wb, { bookType: 'xlsx', type: 'array' });
                saveAs(new Blob([wbout], { type: "application/octet-stream" }), 'data.xlsx');
            });
    };

    // ---------- 권한 ----------
    useEffect(() => {
        fetch("/api/getAuthLevel", {
            method: "post",
            headers: { "content-type": "application/json" }
        })
            .then(r => r.json())
            .then(d => setAuthLevel(d.authLevel))
            .catch(() => { });
    }, []);

    // ---------- 데이터 로딩 ----------
    useEffect(() => {
        setSelectedPositions([]);
        setCheckMode(false);

        const req = {
            method: "post",
            headers: { "content-type": "application/json" },
            body: JSON.stringify({ vessel, line, box })
        };

        fetch("/api/getBoxData", req).then(r => r.json()).then(setBoxData);
        fetch("/api/getBoxInfo", req).then(r => r.json()).then(d => setBoxMemo(d[0]?.memo));
    }, [vessel, line, box, refresh]);

    // ---------- 셀/그리드 ----------
    const handleCellClick = (data) => {
        if (!data) return;
        setSelectedOrganoidKey(data.Organoid_Info_Key);
        setSelectedData(data);
    };

    const getCellData = (pos) => boxData.find(d => d.Position === pos) || null;

    const renderGrid = () => {
        const grid = [];
        let position = 1;

        for (let i = 0; i < 10; i++) {
            const row = [];
            for (let j = 0; j < 10; j++) {
                const posStr = String(position);
                const cellData = getCellData(posStr);
                const isChecked = selectedPositions.includes(posStr);
                const cellStyle = cellData ? `${styles.gridCell} ${styles.occupiedCell}` : `${styles.gridCell} ${styles.emptyCell}`;
                const backgroundColor = cellData ? getColorForOrganoid(cellData.Organoid_Info_Key) : 'transparent';

                row.push(
                    <div
                        key={posStr}
                        className={cellStyle}
                        style={{ backgroundColor }}
                        onClick={() => (checkMode ? togglePosition(posStr) : handleCellClick(cellData))}
                    >
                        {checkMode && (
                            <input
                                type="checkbox"
                                className={styles.cellCheckbox}
                                checked={isChecked}
                                onChange={() => togglePosition(posStr)}
                                onClick={(e) => e.stopPropagation()}
                            />
                        )}
                        <span className={styles.cellNumber}>{posStr}</span>
                    </div>
                );
                position++;
            }
            grid.push(<div key={i} className={styles.gridRow}>{row}</div>);
        }
        return grid;
    };

    const renderSelectedCells = () => {
        if (!selectedOrganoidKey) return null;
        const backgroundColor = getColorForOrganoid(selectedOrganoidKey);
        const selectedCells = boxData.filter(d => d.Organoid_Info_Key === selectedOrganoidKey);
        const cellStyle = `${styles.gridCell2} ${styles.occupiedCell}`;
        return (
            <div className={styles.selectedList}>
                {selectedCells.map((cell, idx) => (
                    <div key={idx} className={cellStyle} style={{ backgroundColor }}>
                        <span className={styles.cellNumber}>{cell.Position}</span>
                    </div>
                ))}
            </div>
        );
    };
    // ---------- 렌더 ----------
    return (
        <div className={styles.storageBoxMain}>
            <div className={styles.leftBox}>
                {/* ★ 번역 적용 (Vessel, Line, Box) */}
                <div className={styles.vesselName}>{t('Vessel')}-{vessel}</div>
                <div className={styles.lineBoxContainer}>
                    <div className={styles.lineBoxName}>{t('Line')}: {line}</div>
                    <div className={styles.lineBoxName}>{t('Box')}: {box}</div>
                </div>

                {/* ★ 번역 적용 (Memo) */}
                <div className={styles.memo}>
                    {t('Memo')} : {boxMemo}
                    <img src={editPen} className={styles.editPen} onClick={() => setEditMemo(true)} alt="edit memo" />
                </div>
                {editMemo && <EditBoxMemo onClose={() => setEditMemo(false)} onSubmit={saveMemo} />}

                <div className={styles.gridContainer}>{renderGrid()}</div>

                <div className={styles.downloadDiv}>
                    <button className={styles.excelDownloadButton} onClick={() => setCheckMode(m => !m)}>
                        <img src={checkImage} className={styles.downloadIcon} alt="toggle check" />
                        {checkMode ? t('체크모드 종료') : t('낱개 체크')}
                    </button>
                    {/* ★ 번역 적용 (Download -> 다운로드 키 사용) */}
                    <button className={styles.excelDownloadButton} onClick={handleSubmit}>
                        <img src={downloadIcon} className={styles.downloadIcon} alt="download" /> &nbsp;{t('다운로드')}
                    </button>
                </div>

                {renderSelectedCells()}
            </div>

            <div className={styles.rightBox}>
                <BankingInfoTab data={selectedData} authLevel={authLevel} onDelete={() => setRefresh(r => r + 1)} />
            </div>
        </div>
    );
}

export default StorageBox;
